import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { appointmentKeys } from '@/hooks/use-appointments';
import { contractTemplateKeys } from '@/hooks/use-contract-templates';
import { providerKeys } from '@/hooks/use-provider';
import { serviceTemplateKeys } from '@/hooks/use-service-templates';
import { serviceKeys } from '@/hooks/use-services';
import api from '@/services/api';
import {
  ActivityCategory,
  ActivityPackSummary,
} from '@/types/activity-pack';
import {
  AdjustInput,
  ApplyResult,
  DraftPreview,
  InterviewStatus,
  InterviewTurn,
  OnboardingDraft,
  PatchDraftInput,
} from '@/types/onboarding-interview';

const BASE = '/activity-packs/interview';

export const interviewKeys = {
  all: ['onboarding-interview'] as const,
  status: () => [...interviewKeys.all, 'status'] as const,
  draft: () => [...interviewKeys.all, 'draft'] as const,
  preview: () => [...interviewKeys.all, 'preview'] as const,
  packs: (categoryId?: string) =>
    ['activity-packs', 'list', categoryId ?? 'all'] as const,
  categories: () => ['activity-packs', 'categories'] as const,
};

/**
 * O backend devolve `503 { code: 'AGENT_UNAVAILABLE' }` quando o agente está
 * fora — sem chave, API caída, timeout. É esse código que joga o onboarding no
 * fallback sem IA (T-24), inclusive no meio da conversa.
 */
export function isAgentUnavailable(error: unknown): boolean {
  const status = (error as AxiosError)?.response?.status;
  const code = (
    (error as AxiosError<{ code?: string }>)?.response?.data as
      | { code?: string }
      | undefined
  )?.code;
  return status === 503 || code === 'AGENT_UNAVAILABLE';
}

/** O agente está de pé? Perguntado antes de abrir a conversa. */
export function useInterviewStatus() {
  const isHydrated = useAuthHydration();
  return useQuery({
    queryKey: interviewKeys.status(),
    queryFn: async () => {
      const res = await api.get<InterviewStatus>(`${BASE}/status`);
      return res.data;
    },
    enabled: isHydrated,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** O rascunho + o transcript: é com ele que o chat é remontado na retomada. */
export function useInterviewDraft(enabled = true) {
  const isHydrated = useAuthHydration();
  return useQuery({
    queryKey: interviewKeys.draft(),
    queryFn: async () => {
      const res = await api.get<OnboardingDraft>(`${BASE}/draft`);
      return res.data;
    },
    enabled: isHydrated && enabled,
  });
}

/**
 * Um turno da conversa. Texto vazio abre (ou retoma) sem gastar turno — é o
 * que a tela faz ao montar.
 *
 * Não invalida o rascunho a cada turno de propósito: a resposta do turno já
 * traz o estado inteiro, e refazer o `GET /draft` junto duplicaria a ida ao
 * servidor no caminho mais quente da tela.
 */
export function useInterview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (text?: string) => {
      const res = await api.post<InterviewTurn>(`${BASE}/message`, {
        text: text ?? '',
      });
      return res.data;
    },
    onSuccess: turn => {
      if (turn.finished) {
        queryClient.invalidateQueries({ queryKey: interviewKeys.preview() });
      }
    },
  });
}

/**
 * "Pronto" no cartão: o prestador respondeu pelo widget e a conversa anda.
 *
 * Não passa pelo modelo e não gasta turno — a resposta já foi gravada pelo
 * `PATCH /draft`, e o que volta é a próxima pergunta, escrita à mão no
 * backend. É o que impede o agente de ficar repetindo a mesma pergunta depois
 * de o prestador ter respondido por toque.
 */
export function useAdvanceInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<InterviewTurn>(`${BASE}/advance`);
      return res.data;
    },
    onSuccess: turn => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.draft() });
      if (turn.finished) {
        queryClient.invalidateQueries({ queryKey: interviewKeys.preview() });
      }
    },
  });
}

/**
 * O ajuste dirigido depois do resumo.
 *
 * Sem nada, traz o menu de partes; com `slot`, o cartão daquela parte; com
 * `done`, o fecho. Não passa pelo modelo e não gasta turno — antes disto,
 * "Ajustar" reabria a entrevista, que não tinha o que perguntar e devolvia o
 * resumo na mesma hora.
 */
export function useAdjustInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdjustInput = {}) => {
      const res = await api.post<InterviewTurn>(`${BASE}/adjust`, input);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.draft() });
      queryClient.invalidateQueries({ queryKey: interviewKeys.preview() });
    },
  });
}

/** O resumo de confirmação, em linguagem de negócio. Não escreve nada. */
export function useDraftSummary(enabled = true) {
  const isHydrated = useAuthHydration();
  return useQuery({
    queryKey: interviewKeys.preview(),
    queryFn: async () => {
      const res = await api.get<DraftPreview>(`${BASE}/preview`);
      return res.data;
    },
    enabled: isHydrated && enabled,
  });
}

/**
 * A edição por toque no widget: `PATCH` direto no rascunho, sem passar pelo
 * modelo e sem gastar turno. É o que transforma "a barba é 20, não 25" de uma
 * rodada de conversa num toque.
 */
export function usePatchDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PatchDraftInput) => {
      const res = await api.patch<OnboardingDraft>(`${BASE}/draft`, input);
      return res.data;
    },
    onSuccess: draft => {
      queryClient.setQueryData(interviewKeys.draft(), draft);
      queryClient.invalidateQueries({ queryKey: interviewKeys.preview() });
    },
  });
}

/** O caminho sem IA: escolhe o ramo e o pack curado preenche a entrevista. */
export function useAdoptPack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      const res = await api.post<OnboardingDraft>(`${BASE}/adopt-pack`, {
        slug,
      });
      return res.data;
    },
    onSuccess: draft => {
      queryClient.setQueryData(interviewKeys.draft(), draft);
      queryClient.invalidateQueries({ queryKey: interviewKeys.preview() });
    },
  });
}

/**
 * "Confirmar": a única escrita de domínio do fluxo inteiro. Depois dela o
 * prestador tem serviços, grade, regras de agenda, modelos e vocabulário —
 * então tudo isso que já estava em cache está velho.
 */
export function useApplyDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<ApplyResult>(`${BASE}/apply`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.all });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
      queryClient.invalidateQueries({ queryKey: contractTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: serviceTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}

/** "Prefiro configurar sozinho": encerra a conversa sem deixar nada para trás. */
export function useAbandonInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<OnboardingDraft>(`${BASE}/abandon`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}

// ------------------------------------------------------- fallback sem IA

export function useActivityCategories(enabled = true) {
  const isHydrated = useAuthHydration();
  return useQuery({
    queryKey: interviewKeys.categories(),
    queryFn: async () => {
      const res = await api.get<ActivityCategory[]>('/activity-packs/categories');
      return res.data;
    },
    enabled: isHydrated && enabled,
  });
}

export function useActivityPacks(categoryId?: string, enabled = true) {
  const isHydrated = useAuthHydration();
  return useQuery({
    queryKey: interviewKeys.packs(categoryId),
    queryFn: async () => {
      const res = await api.get<ActivityPackSummary[]>('/activity-packs', {
        params: categoryId ? { categoryId } : undefined,
      });
      return res.data;
    },
    enabled: isHydrated && enabled,
  });
}
